import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '@/utils/axiosInstance';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import MultipleSelector from '@/components/ui/multi-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Search, Pencil, Crown, Users } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const PROTECTED_EMAIL = 'info@lamptwist.be';

const WhitelistManagement = () => {
  const { t } = useTranslation('admin');
  const { toast } = useToast();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [domainUsersDialogOpen, setDomainUsersDialogOpen] = useState(false);
  const [domainUsers, setDomainUsers] = useState([]);
  const [domainUsersLoading, setDomainUsersLoading] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('');

  const isProtectedEntry = (entry) => {
    return entry.entry_type === 'email' && entry.value === PROTECTED_EMAIL;
  };

  const [formData, setFormData] = useState({
    entry_type: 'email',
    value: '',
    first_name: '',
    is_active: true,
    is_staff: false,
    allowed_brands: [],
  });

  const filteredEntries = useMemo(() => {
    if (!search) return entries;
    const searchLower = search.toLowerCase();
    return entries.filter(
      (entry) =>
        entry.value.toLowerCase().includes(searchLower) ||
        entry.first_name?.toLowerCase().includes(searchLower) ||
        entry.allowed_brands?.some((b) => b.toLowerCase().includes(searchLower))
    );
  }, [entries, search]);

  // Pagination
  const totalPages = Math.ceil(filteredEntries.length / itemsPerPage);
  const paginatedEntries = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredEntries.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredEntries, currentPage, itemsPerPage]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    fetchEntries();
    fetchBrands();
  }, []);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/admin/whitelist/');
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching whitelist:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axiosInstance.get('/api/brands/');
      setAvailableBrands(response.data.brands || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const handleCreate = async () => {
    try {
      await axiosInstance.post('/api/admin/whitelist/create/', formData);
      toast({ title: t('whitelist.created') });
      setDialogOpen(false);
      resetForm();
      fetchEntries();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.error || t('common.createFailed'),
        variant: 'destructive',
      });
    }
  };

  const handleToggleActive = async (entry) => {
    if (isProtectedEntry(entry)) return;

    try {
      await axiosInstance.patch(`/api/admin/whitelist/${entry.id}/`, {
        is_active: !entry.is_active,
      });
      fetchEntries();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('common.updateFailed'),
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (entryId) => {
    if (!confirm(t('whitelist.confirmDelete'))) return;

    try {
      await axiosInstance.delete(`/api/admin/whitelist/${entryId}/delete/`);
      toast({ title: t('whitelist.deleted') });
      fetchEntries();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('common.deleteFailed'),
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry({
      ...entry,
      allowed_brands: entry.allowed_brands || [],
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingEntry) return;

    try {
      await axiosInstance.patch(`/api/admin/whitelist/${editingEntry.id}/`, {
        first_name: editingEntry.first_name,
        is_active: editingEntry.is_active,
        is_staff: editingEntry.is_staff,
        allowed_brands: editingEntry.allowed_brands,
      });
      toast({ title: t('whitelist.updated') });
      setEditDialogOpen(false);
      setEditingEntry(null);
      fetchEntries();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.error || t('common.updateFailed'),
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      entry_type: 'email',
      value: '',
      first_name: '',
      is_active: true,
      is_staff: false,
      allowed_brands: [],
    });
  };

  const handleShowDomainUsers = async (entry) => {
    setSelectedDomain(entry.value);
    setDomainUsersLoading(true);
    setDomainUsersDialogOpen(true);

    try {
      const response = await axiosInstance.get(`/api/admin/whitelist/${entry.id}/users/`);
      setDomainUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching domain users:', error);
      toast({
        title: t('common.error'),
        description: t('whitelist.failedToLoadUsers'),
        variant: 'destructive',
      });
      setDomainUsers([]);
    } finally {
      setDomainUsersLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('activity.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            {filteredEntries.length} {t('activity.results')}
          </span>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                {t('whitelist.addEntry')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] overflow-visible">
              <DialogHeader>
                <DialogTitle>{t('whitelist.addEntry')}</DialogTitle>
              </DialogHeader>

              <div className="space-y-4 px-6 pb-6">
                {/* Type and Name on same line */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('whitelist.entryType')}</Label>
                    <Select
                      value={formData.entry_type}
                      onValueChange={(v) => setFormData({ ...formData, entry_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">{t('whitelist.email')}</SelectItem>
                        <SelectItem value="domain">{t('whitelist.domain')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('whitelist.name')}</Label>
                    <Input
                      placeholder={t('whitelist.namePlaceholder')}
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('whitelist.value')}</Label>
                  <Input
                    placeholder={formData.entry_type === 'email' ? t('whitelist.emailPlaceholder') : t('whitelist.domainPlaceholder')}
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_staff"
                    checked={formData.is_staff}
                    onCheckedChange={(v) => setFormData({ ...formData, is_staff: v })}
                  />
                  <Label htmlFor="is_staff">{t('whitelist.isAdmin')}</Label>
                </div>

                <div className="space-y-2">
                  <Label>{t('whitelist.brands')}</Label>
                  <MultipleSelector
                    value={formData.allowed_brands.map(brand => ({ value: brand, label: brand }))}
                    onChange={(selected) => setFormData({ ...formData, allowed_brands: selected.map(s => s.value) })}
                    defaultOptions={availableBrands.map(brand => ({ value: brand, label: brand }))}
                    placeholder={t('whitelist.searchBrands')}
                    emptyIndicator={<p className="text-center text-sm">{t('activity.noData')}</p>}
                    hidePlaceholderWhenSelected
                    className="overflow-visible"
                  />
                </div>

                <Button onClick={handleCreate} className="w-full">
                  {t('common.add')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('whitelist.editEntry')}</DialogTitle>
          </DialogHeader>

          {editingEntry && (
            <div className="space-y-4 px-6 pb-6">
              {/* Type and Name on same line */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('whitelist.entryType')}</Label>
                  <Input value={editingEntry.entry_type === 'email' ? t('whitelist.email') : t('whitelist.domain')} disabled />
                </div>

                <div className="space-y-2">
                  <Label>{t('whitelist.name')}</Label>
                  <Input
                    placeholder={t('whitelist.namePlaceholder')}
                    value={editingEntry.first_name || ''}
                    onChange={(e) => setEditingEntry({ ...editingEntry, first_name: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('whitelist.value')}</Label>
                <Input value={editingEntry.value} disabled />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="edit_is_staff"
                  checked={editingEntry.is_staff}
                  onCheckedChange={(v) => setEditingEntry({ ...editingEntry, is_staff: v })}
                />
                <Label htmlFor="edit_is_staff">{t('whitelist.isAdmin')}</Label>
              </div>

              <div className="space-y-2">
                <Label>{t('whitelist.brands')}</Label>
                <MultipleSelector
                  value={editingEntry.allowed_brands.map(brand => ({ value: brand, label: brand }))}
                  onChange={(selected) => setEditingEntry({ ...editingEntry, allowed_brands: selected.map(s => s.value) })}
                  defaultOptions={availableBrands.map(brand => ({ value: brand, label: brand }))}
                  placeholder={t('whitelist.searchBrands')}
                  emptyIndicator={<p className="text-center text-sm">{t('activity.noData')}</p>}
                  hidePlaceholderWhenSelected
                  className="overflow-visible"
                />
              </div>

              <Button onClick={handleUpdate} className="w-full">
                {t('common.save')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Domain Users Dialog */}
      <Dialog open={domainUsersDialogOpen} onOpenChange={setDomainUsersDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{t('whitelist.domainUsers')} {selectedDomain}</DialogTitle>
          </DialogHeader>

          <div className="max-h-[400px] overflow-y-auto">
            {domainUsersLoading ? (
              <div className="text-center py-8">{t('common.loading')}</div>
            ) : domainUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {t('whitelist.noUsersForDomain')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('whitelist.email')}</TableHead>
                    <TableHead>{t('whitelist.name')}</TableHead>
                    <TableHead>{t('whitelist.lastLogin')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {domainUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-mono text-sm">{user.email}</TableCell>
                      <TableCell>{user.full_name || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {user.last_login ? formatDateTime(user.last_login) : t('whitelist.neverConnected')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('whitelist.name')}</TableHead>
              <TableHead>{t('whitelist.entryType')}</TableHead>
              <TableHead>{t('whitelist.value')}</TableHead>
              <TableHead>{t('whitelist.brands')}</TableHead>
              <TableHead>{t('whitelist.lastLogin')}</TableHead>
              <TableHead>{t('whitelist.isActive')}</TableHead>
              <TableHead>{t('whitelist.createdAt')}</TableHead>
              <TableHead className="w-[120px]">{t('whitelist.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : paginatedEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {t('whitelist.noEntries')}
                </TableCell>
              </TableRow>
            ) : (
              paginatedEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {entry.first_name || '-'}
                      {entry.is_staff && <Crown className="h-4 w-4 text-yellow-500" />}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={entry.entry_type === 'email' ? 'default' : 'secondary'}>
                      {entry.entry_type === 'email' ? t('whitelist.email') : t('whitelist.domain')}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono">{entry.value}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {entry.allowed_brands?.slice(0, 3).map((brand) => (
                        <Badge key={brand} variant="secondary" className="text-xs">
                          {brand}
                        </Badge>
                      ))}
                      {entry.allowed_brands?.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{entry.allowed_brands.length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {entry.entry_type === 'email' ? (
                      <span className="text-sm text-muted-foreground">
                        {entry.last_login ? formatDateTime(entry.last_login) : t('whitelist.neverConnected')}
                      </span>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => handleShowDomainUsers(entry)}
                      >
                        <Users className="h-3.5 w-3.5 mr-1" />
                        {entry.user_count || 0} {t('whitelist.users')}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={entry.is_active}
                      onCheckedChange={() => handleToggleActive(entry)}
                      disabled={isProtectedEntry(entry)}
                    />
                  </TableCell>
                  <TableCell>{formatDate(entry.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {!isProtectedEntry(entry) && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(entry)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(entry.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {filteredEntries.length > 0 && (
        <div className="flex items-center justify-between mt-4 px-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">{t('pagination.rowsPerPage')}:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border rounded px-2 py-1 text-sm bg-background"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3"
            >
              {t('pagination.previous')}
            </Button>

            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                  className="w-9 h-9 p-0"
                >
                  {pageNum}
                </Button>
              );
            })}

            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="px-2 text-muted-foreground">...</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  className="w-9 h-9 p-0"
                >
                  {totalPages}
                </Button>
              </>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3"
            >
              {t('pagination.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhitelistManagement;
